import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { toast } from 'react-toastify';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || 'http://localhost:5000';
const API = `${BACKEND_URL}/api`;

const AsteriskConfigPage = () => {
  const [activeTab, setActiveTab] = useState('agi');
  const [configurations, setConfigurations] = useState({
    agi: { content: '', filename: '', path: '', last_modified: '' },
    pjsip: { content: '', filename: '', path: '', last_modified: '' },
    dialplan: { content: '', filename: '', path: '', last_modified: '' }
  });
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [backups, setBackups] = useState([]);

  const configTabs = [
    { id: 'agi', name: 'Script AGI', icon: '🐍', description: 'Script Python de conversation' },
    { id: 'pjsip', name: 'PJSIP', icon: '📞', description: 'Configuration SIP' },
    { id: 'dialplan', name: 'Dialplan', icon: '🔄', description: 'Plan de numérotation' }
  ];

  useEffect(() => {
    loadConfiguration(activeTab);
    loadBackups(activeTab);
  }, [activeTab]);

  const loadConfiguration = async (configType) => {
    try {
      setLoading(true);
      const response = await axios.get(`${API}/asterisk/config/${configType}`);
      setConfigurations(prev => ({
        ...prev,
        [configType]: response.data
      }));
    } catch (error) {
      console.error(`Error loading ${configType} configuration:`, error);
      toast.error(`Erreur lors du chargement de la configuration ${configType}`);
    } finally {
      setLoading(false);
    }
  };

  const loadBackups = async (configType) => {
    try {
      const response = await axios.get(`${API}/asterisk/config/${configType}/backups`);
      setBackups(response.data.backups || []);
    } catch (error) {
      console.error(`Error loading ${configType} backups:`, error);
      setBackups([]);
    }
  };

  const saveConfiguration = async () => {
    try {
      setSaving(true);
      const configData = configurations[activeTab];
      
      const response = await axios.post(`${API}/asterisk/config/${activeTab}`, {
        content: configData.content
      });

      if (response.data.success) {
        let message = `Configuration ${activeTab.toUpperCase()} sauvegardée avec succès !`;
        
        // Show reload status if applicable
        if (response.data.reloaded) {
          message += ` Asterisk ${activeTab === 'pjsip' ? 'PJSIP' : 'Dialplan'} rechargé.`;
          if (response.data.reload_message) {
            console.log('Asterisk reload message:', response.data.reload_message);
          }
        } else if (activeTab === 'agi') {
          message += ' Note: Redémarrez le serveur AGI pour appliquer les changements.';
        }
        
        toast.success(message);
        // Recharger la configuration pour obtenir la nouvelle date de modification
        await loadConfiguration(activeTab);
        await loadBackups(activeTab);
      } else {
        toast.error('Erreur lors de la sauvegarde');
      }
    } catch (error) {
      console.error(`Error saving ${activeTab} configuration:`, error);
      toast.error(`Erreur lors de la sauvegarde de la configuration ${activeTab}`);
    } finally {
      setSaving(false);
    }
  };

  const restoreBackup = async (backupFilename) => {
    if (!window.confirm(`Êtes-vous sûr de vouloir restaurer la sauvegarde ${backupFilename} ?`)) {
      return;
    }

    try {
      setLoading(true);
      const response = await axios.post(`${API}/asterisk/config/${activeTab}/restore/${backupFilename}`);
      
      if (response.data.success) {
        let message = 'Sauvegarde restaurée avec succès !';
        
        // Show reload status if applicable
        if (response.data.reloaded) {
          message += ` Asterisk ${activeTab === 'pjsip' ? 'PJSIP' : 'Dialplan'} rechargé.`;
          if (response.data.reload_message) {
            console.log('Asterisk reload message:', response.data.reload_message);
          }
        }
        
        toast.success(message);
        await loadConfiguration(activeTab);
        await loadBackups(activeTab);
      } else {
        toast.error('Erreur lors de la restauration');
      }
    } catch (error) {
      console.error(`Error restoring backup:`, error);
      toast.error('Erreur lors de la restauration de la sauvegarde');
    } finally {
      setLoading(false);
    }
  };

  const updateContent = (content) => {
    setConfigurations(prev => ({
      ...prev,
      [activeTab]: {
        ...prev[activeTab],
        content: content
      }
    }));
  };

  const getEditorLanguage = (configType) => {
    switch (configType) {
      case 'agi': return 'node';
      case 'pjsip': return 'ini';
      case 'dialplan': return 'ini';
      default: return 'text';
    }
  };

  const currentConfig = configurations[activeTab];

  return (
    <div className="p-8">
      <div className="max-w-7xl mx-auto">
        <div className="mb-8">
          <h1 className="text-3xl font-bold text-gray-900 mb-2">Configuration Asterisk</h1>
          <p className="text-gray-600">Gérez et modifiez les configurations Asterisk directement depuis l'interface</p>
        </div>

        {/* Tabs */}
        <div className="flex space-x-1 bg-gray-100 rounded-lg p-1 mb-6">
          {configTabs.map((tab) => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              className={`flex-1 flex items-center justify-center px-4 py-3 rounded-md transition-colors ${
                activeTab === tab.id
                  ? 'bg-white shadow-sm text-blue-600 font-medium'
                  : 'text-gray-600 hover:text-gray-900'
              }`}
            >
              <span className="text-xl mr-2">{tab.icon}</span>
              <div className="text-left">
                <div className="text-sm font-medium">{tab.name}</div>
                <div className="text-xs opacity-70">{tab.description}</div>
              </div>
            </button>
          ))}
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-4 gap-6">
          {/* Editor */}
          <div className="lg:col-span-3">
            <div className="bg-white rounded-xl shadow-lg">
              {/* Header */}
              <div className="flex items-center justify-between p-6 border-b border-gray-200">
                <div>
                  <h2 className="text-xl font-bold text-gray-900">
                    {configTabs.find(tab => tab.id === activeTab)?.name}
                  </h2>
                  <div className="flex items-center space-x-4 mt-1 text-sm text-gray-500">
                    <span>📄 {currentConfig.filename}</span>
                    {/* <span>📍 {currentConfig.path}</span> */}
                    {currentConfig.last_modified && (
                      <span>🕒 {new Date(currentConfig.last_modified).toLocaleString('fr-FR')}</span>
                    )}
                  </div>
                </div>
                <div className="flex space-x-3">
                  <button
                    onClick={() => loadConfiguration(activeTab)}
                    disabled={loading}
                    className="px-4 py-2 text-gray-600 hover:text-gray-800 border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors disabled:opacity-50"
                  >
                    {loading ? '🔄' : '↻'} Recharger
                  </button>
                  <button
                    onClick={saveConfiguration}
                    disabled={saving || loading}
                    className="px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors disabled:opacity-50"
                  >
                    {saving ? '💾 Sauvegarde...' : '💾 Sauvegarder'}
                  </button>
                </div>
              </div>

              {/* Editor */}
              <div className="p-0">
                {loading ? (
                  <div className="flex items-center justify-center h-96">
                    <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
                  </div>
                ) : (
                  <div className="relative">
                    <textarea
                      value={currentConfig.content || ''}
                      onChange={(e) => updateContent(e.target.value)}
                      className="w-full h-96 p-4 font-mono text-sm border-0 resize-none focus:outline-none focus:ring-0"
                      placeholder={`Contenu de la configuration ${activeTab.toUpperCase()}...`}
                      style={{
                        backgroundColor: '#1e1e1e',
                        color: '#d4d4d4',
                        fontFamily: 'Monaco, Menlo, "Ubuntu Mono", monospace'
                      }}
                    />
                    <div className="absolute top-2 right-2 text-xs text-gray-500 bg-gray-100 px-2 py-1 rounded">
                      {getEditorLanguage(activeTab)}
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* Sidebar */}
          <div className="space-y-6">
            {/* Actions */}
            <div className="bg-white rounded-xl shadow-lg p-6">
              <h3 className="text-lg font-bold text-gray-900 mb-4">Actions</h3>
              <div className="space-y-3">
                <button
                  onClick={() => loadConfiguration(activeTab)}
                  disabled={loading}
                  className="w-full flex items-center justify-center px-4 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 transition-colors disabled:opacity-50"
                >
                  <span className="mr-2">🔄</span>
                  Recharger
                </button>
                <button
                  onClick={saveConfiguration}
                  disabled={saving || loading}
                  className="w-full flex items-center justify-center px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors disabled:opacity-50"
                >
                  <span className="mr-2">💾</span>
                  {saving ? 'Sauvegarde...' : 'Sauvegarder'}
                </button>
              </div>
            </div>

            {/* Backups */}
            <div className="bg-white rounded-xl shadow-lg p-6">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-lg font-bold text-gray-900">Sauvegardes</h3>
                <button
                  onClick={() => loadBackups(activeTab)}
                  className="text-gray-500 hover:text-gray-700"
                >
                  🔄
                </button>
              </div>
              
              <div className="space-y-2 max-h-64 overflow-y-auto">
                {backups.length > 0 ? (
                  backups.map((backup, index) => (
                    <div key={index} className={`p-3 rounded-lg border ${index === 0 ? 'border-blue-200 bg-blue-50' : 'border-gray-200'}`}>
                      <div className="flex items-center justify-between">
                        <div>
                          <div className="text-sm font-medium text-gray-900">
                            {backup.filename.replace(currentConfig.filename + '.backup.', '')}
                          </div>
                          <div className="text-xs text-gray-500">
                            {new Date(backup.created).toLocaleString('fr-FR')}
                          </div>
                          <div className="text-xs text-gray-400">
                            {(backup.size / 1024).toFixed(1)} KB
                          </div>
                        </div>
                        <button
                          onClick={() => restoreBackup(backup.filename)}
                          className="text-blue-600 hover:text-blue-800 text-sm font-medium"
                        >
                          Restaurer
                        </button>
                      </div>
                    </div>
                  ))
                ) : (
                  <div className="text-center text-gray-500 text-sm py-4">
                    Aucune sauvegarde disponible
                  </div>
                )}
              </div>
            </div>

            {/* Info */}
            <div className="bg-yellow-50 rounded-xl p-6 border border-yellow-200">
              <h3 className="text-lg font-bold text-yellow-800 mb-3">⚠️ Attention</h3>
              <div className="text-sm text-yellow-700 space-y-2">
                <p>• Les modifications sont automatiquement sauvegardées</p>
                <p>• Une sauvegarde est créée avant chaque modification</p>
                <p>• Les changements PJSIP/Dialplan rechargent Asterisk</p>
                <p>• Testez toujours après les modifications</p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AsteriskConfigPage;